import React, { useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { EventPageType } from '@graphql/generated-base';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { createContext } from '@shared/utils/createContext';
import { isInIframe } from '@shared/utils/isInIframe';
import { isRegistryPage, selectRoutedPageKind } from '@shared/utils/migratedCustomPages';
import { useActiveTargetSlug } from '@apps/guest/packages/layout-engine/components/PageContentVisibilityProvider';
import { GuestPageContainerBySlugDocument, GuestPageContainersBySlugsDocument } from './Custom.graphql.generated';
import type { GuestPageContainerBySlugQuery, GuestPageContainersBySlugsQuery, GuestPageContainersBySlugsQueryVariables } from './Custom.graphql.generated';
import type { CustomDataPage } from './utils/customDataRequest';
import { resolveCustomDataRequest, toCustomDataVariables } from './utils/customDataRequest';
import { useIsStaysRedesignActive } from './components/Honeymoon/useIsStaysRedesignActive';

// One section gains nothing from batching; the backend caps a request at 50 slugs.
const MIN_BULK_SLUGS = 2;
const MAX_BULK_SLUGS = 50;
// Longest the covered sections wait for a still-loading flag before fetching on their own.
const FLAG_WAIT_TIMEOUT_MS = 3000;

// awaitingFlag: flag still loading on mount, covered sections held. fetching: bulk request in flight.
// done: sections fetch normally, and never wait again.
type BulkPhase = 'awaitingFlag' | 'fetching' | 'done';

type BulkCustomPageDataContextValue = Readonly<{
  /** Container slugs the bulk request covers. */
  coveredContainerSlugs: ReadonlySet<string>;
  /** True while the flag decision or the bulk request is outstanding. */
  isPending: boolean;
}>;

const [ContextProvider, useContextValue] = createContext<BulkCustomPageDataContextValue>({
  name: 'BulkCustomPageData',
  // Outside a stacked layout nothing is covered, so every widget fetches on its own.
  initialValue: { coveredContainerSlugs: new Set(), isPending: false },
});

// Container slugs of the sections that render the Custom widget, minus the landing target (it is
// prefetched unbatched for TTFB) and contested slugs (their widget may need a `pageKind`).
const selectBulkContainerSlugs = (pages: ReadonlyArray<EventPageFragment>, eventPages: ReadonlyArray<CustomDataPage>, targetSlug: string | undefined): ReadonlyArray<string> => {
  const containerSlugs = new Set<string>();
  pages.forEach(({ type, pageSlug }) => {
    // Migrated registrylinks renders the registry list, not the Custom widget.
    if (type !== EventPageType.custom || isRegistryPage(type, pageSlug) || pageSlug === targetSlug) return;
    if (selectRoutedPageKind(eventPages, pageSlug, true)) return;
    const { containerSlug } = resolveCustomDataRequest(pageSlug, eventPages, false);
    if (containerSlug) containerSlugs.add(containerSlug);
  });
  return [...containerSlugs].slice(0, MAX_BULK_SLUGS);
};

type BulkCustomPageDataProviderProps = React.PropsWithChildren<{
  eventHandle: string;
  /** The sections the layout renders. */
  pages: ReadonlyArray<EventPageFragment>;
  /** `event.pages`, the list the Custom widget resolves its container slug against. */
  eventPages: ReadonlyArray<CustomDataPage>;
}>;

/**
 * Stacked layouts mount every custom page section at once. Behind `guestSiteBulkCustomPagesEnabled`
 * this loads them in one `pageContainersBySlugs` request and writes each entry under its widget's
 * GuestPageContainerBySlug cache key, so the widgets' own cache-first queries never hit the network.
 */
export const BulkCustomPageDataProvider: React.FC<BulkCustomPageDataProviderProps> = ({ eventHandle, pages, eventPages, children }) => {
  const client = useApolloClient();
  const { isAdmin } = useEventUserRole();
  const isAdminDashboard = isInIframe() && isAdmin;
  const targetSlug = useActiveTargetSlug();
  // The honeymoon widget's query participates in SSR; holding it back would drop that.
  const isStaysRedesignActive = useIsStaysRedesignActive();

  const containerSlugs = useMemo(
    () => (isStaysRedesignActive ? [] : selectBulkContainerSlugs(pages, eventPages, targetSlug)),
    [isStaysRedesignActive, pages, eventPages, targetSlug],
  );
  const hasEnoughSlugs = containerSlugs.length >= MIN_BULK_SLUGS;
  const [isEnabled, isFlagLoading] = useIsFeatureEnabled('guestSiteBulkCustomPagesEnabled', { skip: !hasEnoughSlugs });

  // Only the first load can be bulk-fetched: decided on mount, or once a flag still loading then
  // settles. A request starting later (an editor or sign-in growing the page list) would blank
  // sections that already rendered.
  const [phase, setPhase] = useState<BulkPhase>(() => {
    if (!hasEnoughSlugs) return 'done';
    if (isFlagLoading) return 'awaitingFlag';
    return isEnabled ? 'fetching' : 'done';
  });

  useEffect(() => {
    if (phase !== 'awaitingFlag') return;
    if (!isFlagLoading) {
      setPhase(isEnabled && hasEnoughSlugs ? 'fetching' : 'done');
      return;
    }
    const timer = setTimeout(() => setPhase('done'), FLAG_WAIT_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [phase, isFlagLoading, isEnabled, hasEnoughSlugs]);

  // Serialized so a re-render with equal pages doesn't refire the request.
  const requestKey = phase === 'fetching' ? JSON.stringify({ eventHandle, isAdminDashboard, containerSlugs }) : null;
  const isPending = phase === 'awaitingFlag' || !!requestKey;

  useEffect(() => {
    if (!requestKey) return;
    let isCancelled = false;
    const request: { eventHandle: string; isAdminDashboard: boolean; containerSlugs: string[] } = JSON.parse(requestKey);
    client
      .query<GuestPageContainersBySlugsQuery, GuestPageContainersBySlugsQueryVariables>({
        query: GuestPageContainersBySlugsDocument,
        variables: { payload: { eventName: request.eventHandle, pageSlugs: request.containerSlugs }, isAdminDashboard: request.isAdminDashboard },
        // Not cached: a remount would otherwise write this (possibly stale) list back over fresher entries.
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      })
      .then(({ data, errors }) => {
        // Any entry, null or not, may be partial under an error; leave them all to their widgets.
        if (isCancelled || errors?.length) return;
        data?.pageContainersBySlugs.forEach((entry, index) => {
          const containerSlug = request.containerSlugs[index];
          if (!containerSlug) return;
          client.writeQuery<GuestPageContainerBySlugQuery>({
            query: GuestPageContainerBySlugDocument,
            variables: toCustomDataVariables(request.eventHandle, containerSlug, request.isAdminDashboard, undefined),
            data: { pageContainerBySlug: entry },
          });
        });
      })
      // On failure the cache stays empty and each widget falls back to its own request.
      .catch(() => undefined)
      .finally(() => {
        if (!isCancelled) setPhase('done');
      });
    return () => {
      isCancelled = true;
    };
  }, [client, requestKey]);

  const value = useMemo<BulkCustomPageDataContextValue>(() => ({ coveredContainerSlugs: new Set(isPending ? containerSlugs : []), isPending }), [isPending, containerSlugs]);

  return <ContextProvider value={value}>{children}</ContextProvider>;
};

/** True while the bulk request that covers this widget's fetch is in flight. */
export const useIsWaitingForBulkCustomPageData = (containerSlug: string, routedPageKind: CustomDataPage['pageKind']): boolean => {
  const { coveredContainerSlugs, isPending } = useContextValue();
  return isPending && !routedPageKind && coveredContainerSlugs.has(containerSlug);
};
